import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { Users, ShieldCheck, Link2, Activity, Search, RefreshCw } from "lucide-react";

/**
 * Admin — internal users list, gated to ADMIN_EMAIL server-side.
 * Renders a full table on desktop and stacked cards on mobile so it stays
 * readable on any device. Text sizes bumped up per the responsive brief.
 */
const AdminUsers = () => {
    const { user, language } = useApp();
    const navigate = useNavigate();
    const isEs = language === "es";
    const [state, setState] = useState({ loading: true, error: null, data: null });
    const [q, setQ] = useState("");

    // Client-side guard — the API also enforces this, but this avoids a flash
    // of the empty admin page for non-admin users who type the URL.
    useEffect(() => {
        if (user && user.is_admin === false) navigate("/dashboard", { replace: true });
    }, [user, navigate]);

    const load = async () => {
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const r = await api.get("/admin/users");
            setState({ loading: false, error: null, data: r.data });
        } catch (e) {
            setState({
                loading: false,
                error: e?.response?.data?.detail || (isEs ? "Error al cargar" : "Failed to load"),
                data: null,
            });
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    if (!user || user.is_admin === false) return null;

    const rows = (state.data?.users || []).filter((u) => {
        if (!q) return true;
        const needle = q.toLowerCase();
        return (u.email || "").toLowerCase().includes(needle) ||
               (u.name  || "").toLowerCase().includes(needle);
    });

    const fmtDate = (iso) => {
        if (!iso) return "—";
        try {
            const d = new Date(iso);
            return d.toLocaleDateString(isEs ? "es-ES" : "en-US", {
                year: "numeric", month: "short", day: "numeric",
            });
        } catch { return "—"; }
    };

    const total = state.data?.total ?? 0;
    const plaidN = state.data?.plaid_connected ?? 0;

    return (
        <AppLayout title={isEs ? "Admin" : "Admin"}>
            <div className="max-w-6xl mx-auto space-y-6 md:space-y-8" data-testid="admin-users-page">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="inline-flex items-center gap-2 text-xs md:text-sm font-bold tracking-widest uppercase text-[hsl(var(--primary))]">
                            <ShieldCheck className="w-4 h-4" />
                            {isEs ? "Panel de administrador" : "Admin panel"}
                        </div>
                        <h1 className="mt-2 font-display font-black text-3xl md:text-4xl lg:text-5xl text-[hsl(var(--foreground))] leading-tight">
                            {isEs ? "Suscriptores" : "Subscribers"}
                        </h1>
                        <p className="mt-2 text-base md:text-lg text-[hsl(var(--muted-foreground))]">
                            {isEs
                                ? "Todos los usuarios que iniciaron sesión con Google."
                                : "Everyone who has signed in with Google."}
                        </p>
                    </div>
                    <button
                        data-testid="admin-refresh"
                        onClick={load}
                        className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--secondary))]/70 text-[hsl(var(--foreground))] px-4 py-2 text-sm md:text-base font-semibold transition"
                    >
                        <RefreshCw className={`w-4 h-4 ${state.loading ? "animate-spin" : ""}`} />
                        {isEs ? "Actualizar" : "Refresh"}
                    </button>
                </div>

                {/* Stat cards */}
                <div className="grid grid-cols-2 gap-3 md:gap-5">
                    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 md:p-6" data-testid="admin-stat-total">
                        <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]">
                            <Users className="w-4 h-4" />
                            <span className="text-xs md:text-sm font-semibold uppercase tracking-widest">
                                {isEs ? "Usuarios" : "Users"}
                            </span>
                        </div>
                        <div className="mt-2 font-display font-black text-3xl md:text-5xl text-[hsl(var(--foreground))]">
                            {total}
                        </div>
                    </div>
                    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 md:p-6" data-testid="admin-stat-plaid">
                        <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]">
                            <Link2 className="w-4 h-4" />
                            <span className="text-xs md:text-sm font-semibold uppercase tracking-widest">
                                {isEs ? "Con Plaid" : "Plaid linked"}
                            </span>
                        </div>
                        <div className="mt-2 font-display font-black text-3xl md:text-5xl text-[hsl(var(--foreground))]">
                            {plaidN}
                            <span className="text-base md:text-lg text-[hsl(var(--muted-foreground))] font-semibold ml-2">
                                / {total}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Search */}
                <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[hsl(var(--muted-foreground))]" />
                    <input
                        data-testid="admin-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={isEs ? "Buscar por email o nombre…" : "Search by email or name…"}
                        className="w-full pl-12 pr-4 py-3 md:py-4 rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] text-base md:text-lg text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                    />
                </div>

                {state.error && (
                    <div className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-200 p-4 text-base" data-testid="admin-error">
                        {state.error}
                    </div>
                )}

                {/* Mobile cards */}
                <div className="grid grid-cols-1 gap-3 md:hidden">
                    {rows.map((u) => (
                        <div key={u.user_id}
                             data-testid={`admin-user-card-${u.user_id}`}
                             className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-11 h-11 rounded-full bg-[hsl(var(--secondary))] grid place-items-center overflow-hidden shrink-0">
                                    {u.picture ? (
                                        <img src={u.picture} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <span className="text-base font-bold text-[hsl(var(--primary))]">
                                            {(u.name?.[0] || u.email?.[0] || "?").toUpperCase()}
                                        </span>
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="font-bold text-base text-[hsl(var(--foreground))] truncate">
                                        {u.name || u.email}
                                    </div>
                                    <div className="text-sm text-[hsl(var(--muted-foreground))] truncate">
                                        {u.email}
                                    </div>
                                </div>
                                {u.plaid_connected && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-400 px-2 py-1 text-xs font-bold uppercase tracking-wider">
                                        <Link2 className="w-3 h-3" /> Plaid
                                    </span>
                                )}
                            </div>
                            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
                                        {isEs ? "Alta" : "Signup"}
                                    </div>
                                    <div className="text-sm font-semibold text-[hsl(var(--foreground))]">
                                        {fmtDate(u.created_at)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
                                        {isEs ? "Último" : "Last seen"}
                                    </div>
                                    <div className="text-sm font-semibold text-[hsl(var(--foreground))]">
                                        {fmtDate(u.last_login)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
                                        {isEs ? "Txns" : "Txns"}
                                    </div>
                                    <div className="text-sm font-semibold text-[hsl(var(--foreground))]">
                                        {u.transactions_count}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    {!state.loading && rows.length === 0 && (
                        <div className="text-center py-10 text-[hsl(var(--muted-foreground))] text-base">
                            {isEs ? "Sin usuarios." : "No users."}
                        </div>
                    )}
                </div>

                {/* Desktop table */}
                <div className="hidden md:block rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-[hsl(var(--secondary))]/50 text-[hsl(var(--muted-foreground))] text-xs uppercase tracking-widest">
                                <tr>
                                    <th className="px-5 py-4 font-semibold">{isEs ? "Usuario" : "User"}</th>
                                    <th className="px-5 py-4 font-semibold">{isEs ? "Alta" : "Signed up"}</th>
                                    <th className="px-5 py-4 font-semibold">{isEs ? "Última sesión" : "Last seen"}</th>
                                    <th className="px-5 py-4 font-semibold text-center">Plaid</th>
                                    <th className="px-5 py-4 font-semibold text-right">
                                        <span className="inline-flex items-center gap-1">
                                            <Activity className="w-3.5 h-3.5" /> Txns
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[hsl(var(--border))] text-base">
                                {rows.map((u) => (
                                    <tr key={u.user_id}
                                        data-testid={`admin-user-row-${u.user_id}`}
                                        className="hover:bg-[hsl(var(--secondary))]/40 transition-colors">
                                        <td className="px-5 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-[hsl(var(--secondary))] grid place-items-center overflow-hidden shrink-0">
                                                    {u.picture ? (
                                                        <img src={u.picture} alt="" className="w-full h-full object-cover" />
                                                    ) : (
                                                        <span className="text-sm font-bold text-[hsl(var(--primary))]">
                                                            {(u.name?.[0] || u.email?.[0] || "?").toUpperCase()}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="font-bold text-[hsl(var(--foreground))] truncate">
                                                        {u.name || "—"}
                                                    </div>
                                                    <div className="text-sm text-[hsl(var(--muted-foreground))] truncate">
                                                        {u.email}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-5 py-4 text-[hsl(var(--foreground))]/85 whitespace-nowrap">
                                            {fmtDate(u.created_at)}
                                        </td>
                                        <td className="px-5 py-4 text-[hsl(var(--foreground))]/85 whitespace-nowrap">
                                            {fmtDate(u.last_login)}
                                        </td>
                                        <td className="px-5 py-4 text-center">
                                            {u.plaid_connected ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-400 px-2.5 py-1 text-xs font-bold uppercase tracking-wider">
                                                    <Link2 className="w-3 h-3" />
                                                    {isEs ? "Sí" : "Yes"}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-[hsl(var(--muted-foreground))]">—</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-4 text-right font-bold text-[hsl(var(--foreground))]">
                                            {u.transactions_count}
                                        </td>
                                    </tr>
                                ))}
                                {!state.loading && rows.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="px-5 py-10 text-center text-[hsl(var(--muted-foreground))]">
                                            {isEs ? "Sin usuarios." : "No users."}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

export default AdminUsers;
